import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../../ui/card'
import { Button } from '../../../ui/button'
import { Input } from '../../../ui/input'
import { Label } from '../../../ui/label'
import { Badge } from '../../../ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../ui/table'
import { Users, Plus, Settings, Calendar } from 'lucide-react'
import EventEndpoints from '../../../../endpoints/EventEndpoints'
import { useEventContext } from '../../EventContext'
import { defaultRoleRequest } from '../../config/Roles'
import { SetupStepState } from '../eventSetupStatus'
import { toast } from 'sonner'

const NO_ACCREDITATION = 'none'

interface RolesStepProps {
  step: SetupStepState
}

/**
 * Slim role creator: just the fields needed to get a schedulable role in place.
 * Badge overrides, app access and the rest live on the Roles tab, which this
 * links out to — the wizard deliberately doesn't duplicate that form.
 */
export const RolesStep: React.FC<RolesStepProps> = ({ step }) => {
  const { event, setEvent, canEdit } = useEventContext()
  const [internalName, setInternalName] = useState('')
  const [badgeName, setBadgeName] = useState('')
  const [accreditationId, setAccreditationId] = useState<string>(NO_ACCREDITATION)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  if (!event) return null

  const roles = [...(event.roles || [])].sort(
    (a, b) =>
      Number(b.schedulable) - Number(a.schedulable) ||
      a.internalName.localeCompare(b.internalName)
  )

  const handleAdd = async () => {
    // Badge name isn't required here: it falls back to the role name, which is
    // what most clients want anyway.
    if (!internalName.trim()) {
      setErrors({ internalName: 'Name is required' })
      return
    }

    setSaving(true)
    try {
      const result = await EventEndpoints.addEventRole(event.id, {
        ...defaultRoleRequest,
        internalName: internalName.trim(),
        // Most roles print the same name on the badge; default to it rather
        // than making the client type it twice.
        badgeName: (badgeName.trim() || internalName.trim()),
        defaultAccreditationLevelId:
          accreditationId === NO_ACCREDITATION ? null : parseInt(accreditationId),
        schedulable: true,
      })
      setEvent(result.parent)
      toast.success('Role created')
      setInternalName('')
      setBadgeName('')
      setAccreditationId(NO_ACCREDITATION)
      setErrors({})
    } catch (error: any) {
      const errorMsg =
        error?.response?.data?.message || error?.message || 'Failed to create role'
      toast.error(errorMsg)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Users className="h-4 w-4" />
          Roles
        </CardTitle>
        <CardDescription>{step.help}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {roles.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Badge name</TableHead>
                <TableHead className="text-center">Schedulable</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {roles.map((role) => (
                <TableRow key={role.id}>
                  <TableCell className="font-medium">{role.internalName}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {role.badgeName}
                  </TableCell>
                  <TableCell className="text-center">
                    {role.schedulable ? (
                      <Badge className="gap-1">
                        <Calendar className="h-3 w-3" />
                        Yes
                      </Badge>
                    ) : (
                      <Badge variant="secondary">No</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <p className="py-4 text-center text-sm text-muted-foreground">
            No roles yet. Add one below.
          </p>
        )}

        {canEdit && (
          <div className="space-y-3 rounded-md border p-4">
            <p className="text-sm font-medium">Add a schedulable role</p>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div className="space-y-1.5">
                <Label htmlFor="wizardRoleName">Name *</Label>
                <Input
                  id="wizardRoleName"
                  placeholder="e.g. Gate Staff"
                  value={internalName}
                  onChange={(e) => setInternalName(e.target.value)}
                  disabled={saving}
                />
                {errors.internalName && (
                  <p className="text-xs text-destructive">{errors.internalName}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="wizardRoleBadgeName">Badge name</Label>
                <Input
                  id="wizardRoleBadgeName"
                  placeholder="Defaults to the name"
                  value={badgeName}
                  onChange={(e) => setBadgeName(e.target.value)}
                  disabled={saving}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="wizardRoleAccreditation">
                  Default accreditation
                </Label>
                <Select
                  value={accreditationId}
                  onValueChange={setAccreditationId}
                  disabled={saving}
                >
                  <SelectTrigger id="wizardRoleAccreditation">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_ACCREDITATION}>None</SelectItem>
                    {(event.accreditationLevels || []).map((level) => (
                      <SelectItem key={level.id} value={level.id.toString()}>
                        {level.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-muted-foreground">
                Roles added here are marked schedulable, so the people you provision
                into them can be assigned to shifts.
              </p>
              <Button
                onClick={handleAdd}
                disabled={saving || !internalName.trim()}
                className="shrink-0"
              >
                <Plus className="mr-2 h-4 w-4" />
                {saving ? 'Adding...' : 'Add role'}
              </Button>
            </div>
          </div>
        )}

        <Button asChild variant="ghost" size="sm">
          <Link to={`/event/${event.id}/config/roles`}>
            <Settings className="mr-2 h-3.5 w-3.5" />
            Advanced role settings (badges, app access)
          </Link>
        </Button>
      </CardContent>
    </Card>
  )
}
